/**
 * SSE frame protocol for the notifications stream - pure, React-free.
 *
 * Invalidation-first contract: the client never applies frame payloads to
 * state. Every frame (connected included) means "server state may have
 * changed - refetch over REST". Payloads stay on the wire so a surgical
 * cache-update optimization remains possible without a protocol change.
 */
import type { NotificationOut } from 'connectors/notifications/types.gen';

/** Wire format of one SSE `data:` payload. Producer: notifications/src/pubsub/publisher.py */
export type SSEFrame =
  | { event: 'connected' }
  // The publisher omits server-computed fields like expired; the invalidation client never reads payloads anyway.
  | { event: 'created' | 'updated'; notification: Omit<NotificationOut, 'expired'> }
  | { event: 'resolved'; notification: { id: string } }
  | { event: 'read_state_changed'; notification: { id: string; read_at: string | null } }
  | { event: 'all_read'; read_at: string };

/**
 * Parse one SSE `data:` payload of the named stream. Returns null for
 * malformed payloads: those can only mean a backend serialization bug, so they
 * are logged instead of swallowed silently.
 */
export function parseSseFrame<Frame>(stream: string, data: string): Frame | null {
  try {
    return JSON.parse(data) as Frame;
  } catch {
    console.warn(`[${stream}] malformed SSE frame:`, data.slice(0, 200));
    return null;
  }
}

/** Parse one notifications frame. */
export const parseFrame = (data: string): SSEFrame | null => parseSseFrame<SSEFrame>('notifications', data);
